import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronDown,
  ChevronRight,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Trash2,
} from 'lucide-react';
import { getThreadDisplayTitle } from '../../components/AIChat/threadDisplayTitle';

function groupThreads(threads: any[]) {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayMs = todayStart.getTime();
  const yesterdayMs = todayMs - 86400000;
  const weekMs = todayMs - 6 * 86400000;
  const today: any[] = [];
  const yesterday: any[] = [];
  const thisWeek: any[] = [];
  const older: any[] = [];
  for (const t of threads) {
    const ts = new Date(t.updatedAt ?? t.createdAt).getTime();
    if (ts >= todayMs) today.push(t);
    else if (ts >= yesterdayMs) yesterday.push(t);
    else if (ts >= weekMs) thisWeek.push(t);
    else older.push(t);
  }
  return { today, yesterday, thisWeek, older };
}

function ThreadGroup({
  label,
  threads,
  activeThreadId,
  onSelect,
  onDelete,
}: {
  label: string;
  threads: any[];
  activeThreadId: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  if (!threads.length) return null;
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600 transition-colors hover:text-slate-500"
      >
        {collapsed ? (
          <ChevronRight className="h-2.5 w-2.5 shrink-0" />
        ) : (
          <ChevronDown className="h-2.5 w-2.5 shrink-0" />
        )}
        {label}
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            {threads.map((thread) => {
              const isActive = thread.id === activeThreadId;
              const title = getThreadDisplayTitle(thread);
              return (
                <div
                  key={thread.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(thread.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(thread.id); }
                  }}
                  className={`group relative mx-1 mb-0.5 flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                    isActive
                      ? 'border-brand-purple/25 bg-brand-purple/15'
                      : 'border-transparent hover:bg-dark-elevated'
                  }`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <MessageSquare
                    className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-brand-purple-light' : 'text-slate-600'}`}
                    aria-hidden
                  />
                  <span
                    className={`flex-1 truncate text-xs ${isActive ? 'font-medium text-brand-purple-light' : 'text-slate-400'}`}
                    title={title}
                  >
                    {title}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(thread.id); }}
                    className="hidden h-5 w-5 shrink-0 items-center justify-center rounded text-slate-600 hover:text-red-400 group-hover:flex"
                    aria-label={`Delete "${title}"`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface VantiWindowSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  threads: any[];
  threadsLoading: boolean;
  activeThreadId: string;
  agents: any[];
  selectedAgentId: string;
  onSelectThread: (id: string) => void;
  onDeleteThread: (id: string) => void;
  onNewChat: () => void;
  onSelectAgent: (id: string) => void;
  user?: { username?: string; role?: string };
}

export default function VantiWindowSidebar({
  collapsed,
  onToggleCollapsed,
  threads,
  threadsLoading,
  activeThreadId,
  agents,
  selectedAgentId,
  onSelectThread,
  onDeleteThread,
  onNewChat,
  onSelectAgent,
  user,
}: VantiWindowSidebarProps) {
  const grouped = groupThreads(threads);
  const initial = user?.username?.[0]?.toUpperCase() || 'U';

  /* ── Icon rail (collapsed) ─────────────────────────────────── */
  if (collapsed) {
    return (
      <motion.aside
        initial={false}
        animate={{ width: 52 }}
        className="flex h-full w-[52px] shrink-0 flex-col items-center border-r border-dark-border bg-dark-surface py-3 gap-2"
        aria-label="Sidebar (collapsed)"
      >
        <img src="/assets/Vanti2.png" alt="" className="h-7 w-7 shrink-0 object-contain" aria-hidden />

        <button
          type="button"
          onClick={onNewChat}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-brand-purple/30 bg-brand-purple/10 text-brand-purple-light transition-all hover:bg-brand-purple/20"
          aria-label="New conversation"
          title="New conversation"
        >
          <Plus className="h-4 w-4" aria-hidden />
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onToggleCollapsed}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-dark-elevated hover:text-slate-400"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-4 w-4" aria-hidden />
        </button>

        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-purple text-xs font-semibold text-white"
          title={user?.username || 'User'}
        >
          {initial}
        </div>
      </motion.aside>
    );
  }

  /* ── Full sidebar (expanded) ───────────────────────────────── */
  return (
    <motion.aside
      initial={false}
      animate={{ width: 240 }}
      className="flex h-full w-60 min-w-[15rem] flex-col border-r border-dark-border bg-dark-surface"
      aria-label="Sidebar"
    >
      {/* Logo + new chat */}
      <div className="shrink-0 border-b border-dark-border px-3 py-3">
        <div className="mb-3 flex items-center gap-2.5">
          <img src="/assets/Vanti2.png" alt="" className="h-8 w-8 shrink-0 object-contain" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-100">Vanti</p>
            <p className="text-[10px] text-slate-500">VantagePoint AI</p>
          </div>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-dark-elevated hover:text-slate-400"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center gap-2 rounded-lg border border-brand-purple/30 bg-brand-purple/10 px-3 py-2 text-xs font-medium text-brand-purple-light transition-all hover:border-brand-purple/50 hover:bg-brand-purple/20"
        >
          <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
          New conversation
        </button>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto py-2">
        {threadsLoading && !threads.length ? (
          <p className="px-4 py-3 text-center text-xs text-slate-600">Loading…</p>
        ) : !threads.length ? (
          <p className="px-4 py-3 text-center text-xs text-slate-600">No conversations yet</p>
        ) : (
          <>
            <ThreadGroup label="Today" threads={grouped.today} activeThreadId={activeThreadId} onSelect={onSelectThread} onDelete={onDeleteThread} />
            <ThreadGroup label="Yesterday" threads={grouped.yesterday} activeThreadId={activeThreadId} onSelect={onSelectThread} onDelete={onDeleteThread} />
            <ThreadGroup label="This week" threads={grouped.thisWeek} activeThreadId={activeThreadId} onSelect={onSelectThread} onDelete={onDeleteThread} />
            <ThreadGroup label="Older" threads={grouped.older} activeThreadId={activeThreadId} onSelect={onSelectThread} onDelete={onDeleteThread} />
          </>
        )}
      </div>

      {/* Agents */}
      {agents.length > 0 && (
        <div className="shrink-0 border-t border-dark-border px-3 py-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Agents</p>
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => onSelectAgent('')}
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors ${!selectedAgentId ? 'bg-brand-purple/15 text-brand-purple-light' : 'text-slate-500 hover:bg-dark-elevated hover:text-slate-300'}`}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-brand-purple" aria-hidden />
              Vanti (built-in)
            </button>
            {agents.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelectAgent(a.id)}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors ${selectedAgentId === a.id ? 'bg-brand-purple/15 text-brand-purple-light' : 'text-slate-500 hover:bg-dark-elevated hover:text-slate-300'}`}
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-brand-teal" aria-hidden />
                {a.name}
                {a.visibility === 'org' ? <span className="ml-auto text-[9px] text-slate-600">org</span> : null}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* User footer */}
      <div className="shrink-0 border-t border-dark-border px-3 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-purple text-xs font-semibold text-white">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-slate-300">{user?.username || 'User'}</p>
            {user?.role ? <p className="truncate text-[10px] text-slate-600">{user.role}</p> : null}
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
